import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Loader2, RotateCcw, AlertCircle, Info, ChevronDown, ChevronUp, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { humanizeColumnName } from '../lib/formatters';
import api from '../lib/api';

// ── Polinode-inspired palette (18 distinct community colors) ─────────
const COMMUNITY_PALETTE = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6',
  '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#84cc16',
  '#0ea5e9', '#d946ef', '#22c55e', '#e11d48', '#0891b2',
  '#a855f7', '#64748b', '#fb923c',
];

function scoreToColor(score) {
  if (score < 0.25) return `rgb(${220 - score * 200}, ${60 + score * 300}, 60)`;
  if (score < 0.5) return `rgb(${200 - (score - 0.25) * 400}, ${140 + (score - 0.25) * 200}, ${60 + (score - 0.25) * 200})`;
  if (score < 0.75) return `rgb(${100 - (score - 0.5) * 200}, ${180 - (score - 0.5) * 40}, ${120 + (score - 0.5) * 200})`;
  return `rgb(${50 - (score - 0.75) * 100}, ${170 - (score - 0.75) * 60}, ${180 + (score - 0.75) * 200})`;
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

// ── Structural Metrics ───────────────────────────────────────────────
function StructuralMetrics({ data }) {
  const s = data.structural || {};
  const items = [
    { label: 'Communities', value: data.n_clusters, color: 'text-indigo-600' },
    { label: 'Largest Community', value: `${s.largest_cluster_pct || 0}%`, color: 'text-slate-800' },
    { label: 'Modularity', value: s.modularity?.toFixed(3) || '—', color: s.modularity > 0.4 ? 'text-emerald-600' : s.modularity > 0.2 ? 'text-amber-600' : 'text-red-600' },
    { label: 'Avg Similarity', value: s.avg_similarity?.toFixed(3) || '—', color: 'text-blue-600' },
    { label: 'Isolated Nodes', value: s.isolated_nodes || 0, color: s.isolated_nodes > 50 ? 'text-amber-600' : 'text-slate-600' },
    { label: 'Network Edges', value: s.total_edges?.toLocaleString() || '0', color: 'text-slate-600' },
  ];
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="structural-metrics">
      {items.map(({ label, value, color }) => (
        <div key={label} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={cn("text-base font-bold", color)}>{value}</p>
        </div>
      ))}
    </div>
  );
}

function MetricCard({ label, value, sub, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5">
      <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={cn("text-base font-bold", color || 'text-slate-800')}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500">{sub}</p>}
    </div>
  );
}

// ── Cluster Bubble Summary (accessible HR view) ──────────────────────
function ClusterBubbleSummary({ clusters, onCommunityClick }) {
  if (!clusters || clusters.length === 0) return null;

  // Determine leaning label for each cluster
  const getLeaningStyle = (leaning = '') => {
    const l = leaning.toLowerCase();
    if (l.includes('positive') || l.includes('supportive') || l.includes('agree') || l.includes('strong positive'))
      return { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', ring: 'ring-emerald-200' };
    if (l.includes('negative') || l.includes('oppose') || l.includes('skeptic') || l.includes('disagree'))
      return { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', ring: 'ring-red-200' };
    if (l.includes('neutral') || l.includes('mixed'))
      return { bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-600', ring: 'ring-slate-200' };
    return { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', ring: 'ring-blue-200' };
  };

  const maxPct = Math.max(...clusters.map(c => c.pct));

  return (
    <Card className="border border-slate-200 dark:border-slate-700" data-testid="cluster-bubble-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-800 dark:text-slate-100">Behavioral Segments at a Glance</CardTitle>
        <p className="text-xs text-slate-400">Click a segment to highlight it in the network. Bubble size = population share.</p>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-center gap-6 flex-wrap py-4" data-testid="bubble-container">
          {clusters.map(c => {
            const style = getLeaningStyle(c.leaning);
            const color = COMMUNITY_PALETTE[c.id % COMMUNITY_PALETTE.length];
            // Bubble size proportional to population % (min 64px, max 140px)
            const size = Math.max(64, Math.min(140, (c.pct / maxPct) * 140));

            return (
              <button
                key={c.id}
                onClick={() => onCommunityClick(c.id)}
                className={cn(
                  "flex flex-col items-center gap-2 group transition-transform hover:scale-105 focus:outline-none",
                )}
                data-testid={`bubble-cluster-${c.id}`}
              >
                {/* Bubble */}
                <div
                  className={cn(
                    "rounded-full flex items-center justify-center border-2 transition-shadow group-hover:ring-4",
                    style.bg, style.border, style.ring,
                  )}
                  style={{ width: size, height: size }}
                >
                  <div className="text-center px-1">
                    <span className={cn("text-lg font-black tabular-nums block", style.text)}>
                      {c.pct}%
                    </span>
                  </div>
                </div>

                {/* Label */}
                <div className="text-center max-w-[120px]">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                      Cluster {c.id + 1}
                    </span>
                  </div>
                  <p className={cn("text-[10px] font-medium", style.text)}>{c.leaning}</p>
                  <p className="text-[10px] text-slate-400 truncate" title={c.dominant_response}>{c.dominant_response}</p>
                  {c.dominant_demographics?.length > 0 && (
                    <p className="text-[9px] text-slate-400 truncate mt-0.5" title={c.dominant_demographics[0]?.value}>
                      {c.dominant_demographics[0]?.value}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-400">
          <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-emerald-200 border border-emerald-300" /><span>Supportive</span></div>
          <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-red-200 border border-red-300" /><span>Skeptical</span></div>
          <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-slate-200 border border-slate-300" /><span>Neutral/Mixed</span></div>
        </div>
      </CardContent>
    </Card>
  );
}



// ── Main Component ───────────────────────────────────────────────────
export function BehavioralClusterNetwork({ simulationId }) {
  const graphRef = useRef();
  const containerRef = useRef();
  const roRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [colorMode, setColorMode] = useState('cluster');
  const [sizeMode, setSizeMode] = useState('degree');
  const [filterDim, setFilterDim] = useState('__all__');
  const [filterVal, setFilterVal] = useState('__all__');
  const [pinnedNode, setPinnedNode] = useState(null);
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [edgeDensity, setEdgeDensity] = useState(30);
  const [dimensions, setDimensions] = useState({ width: 0, height: 600 });
  const [legendCollapsed, setLegendCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  useEffect(() => {
    if (!simulationId) return;
    setLoading(true);
    api.get(`/simulations/${simulationId}/behavioral-clusters`, { _silent: true })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [simulationId]);

  const handleContainerRef = useCallback((node) => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    containerRef.current = node;
    if (node) {
      const w = node.clientWidth;
      if (w > 0) setDimensions(prev => ({ ...prev, width: w }));
      roRef.current = new ResizeObserver(entries => {
        const { width } = entries[0].contentRect;
        if (width > 0) setDimensions(prev => ({ ...prev, width }));
      });
      roRef.current.observe(node);
    }
  }, []);
  useEffect(() => () => { if (roRef.current) roRef.current.disconnect(); }, []);

  useEffect(() => {
    setPinnedNode(null); setHighlightNodes(new Set()); setHighlightLinks(new Set());
  }, [filterDim, filterVal]);

  // ── Filtered data ──────────────────────────────────────────────────
  const { filteredNodes, filteredEdges } = useMemo(() => {
    if (!data?.has_clusters) return { filteredNodes: [], filteredEdges: [] };
    let nodes = data.nodes;
    if (filterDim !== '__all__' && filterVal !== '__all__') {
      nodes = nodes.filter(n => String((n.attributes || {})[filterDim]) === filterVal);
    }
    const idSet = new Set(nodes.map(n => n.id));
    const edges = data.edges.filter(e => idSet.has(e.source) && idSet.has(e.target));
    return { filteredNodes: nodes, filteredEdges: edges };
  }, [data, filterDim, filterVal]);

  // ── Build graph data with cluster positioning ──────────────────────
  const graphData = useMemo(() => {
    if (!filteredNodes.length) return { nodes: [], links: [] };
    const sorted = [...filteredEdges].sort((a, b) => b.weight - a.weight);
    const numToShow = edgeDensity === 0 ? 0 : Math.max(1, Math.ceil(sorted.length * edgeDensity / 100));
    const links = sorted.slice(0, numToShow).map(e => ({
      source: e.source, target: e.target, weight: e.weight,
    }));

    // Position nodes in cluster islands (wider separation than before)
    const nClusters = data?.n_clusters || 1;
    const radius = Math.max(Math.sqrt(filteredNodes.length) * 12, 200);
    const clusterCenters = {};
    for (let i = 0; i < nClusters; i++) {
      const angle = (2 * Math.PI * i) / nClusters;
      clusterCenters[i] = { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
    }

    // Count per cluster for spread
    const clusterCounts = {};
    filteredNodes.forEach(n => { clusterCounts[n.cluster] = (clusterCounts[n.cluster] || 0) + 1; });

    const nodes = filteredNodes.map(n => {
      const center = clusterCenters[n.cluster] || { x: 0, y: 0 };
      const count = clusterCounts[n.cluster] || 1;
      const spread = Math.sqrt(count) * 6;
      const jAngle = Math.random() * 2 * Math.PI;
      const jDist = Math.random() * spread;
      return {
        ...n,
        x: center.x + Math.cos(jAngle) * jDist,
        y: center.y + Math.sin(jAngle) * jDist,
      };
    });
    return { nodes, links };
  }, [filteredNodes, filteredEdges, edgeDensity, data]);

  const filterValues = useMemo(() => {
    if (!data?.filter_dimensions || filterDim === '__all__') return [];
    return data.filter_dimensions.find(d => d.name === filterDim)?.values || [];
  }, [data, filterDim]);

  // ── Color logic ────────────────────────────────────────────────────
  const getNodeColor = useCallback((node) => {
    if (colorMode === 'cluster') return COMMUNITY_PALETTE[node.cluster % COMMUNITY_PALETTE.length];
    if (colorMode === 'response') return scoreToColor(node.expected_score);
    const val = String((node.attributes || {})[colorMode] || '');
    let hash = 0;
    for (let i = 0; i < val.length; i++) hash = ((hash << 5) - hash + val.charCodeAt(i)) | 0;
    return COMMUNITY_PALETTE[Math.abs(hash) % COMMUNITY_PALETTE.length];
  }, [colorMode]);

  // ── Canvas node painting (Polinode style) ──────────────────────────
  const paintNode = useCallback((node, ctx, globalScale) => {
    const isHighlighted = highlightNodes.size === 0 || highlightNodes.has(node.id);
    const isPinned = pinnedNode?.id === node.id;

    // Size based on mode
    let baseSize;
    if (sizeMode === 'degree') {
      baseSize = 2 + Math.sqrt(node.degree_centrality || 0.01) * 18;
    } else if (sizeMode === 'extremity') {
      baseSize = 2 + (node.opinion_extremity || 0) * 8;
    } else {
      baseSize = 2 + (node.confidence || 0.5) * 5;
    }

    // Scale down for large networks
    if (filteredNodes.length > 800) baseSize *= 0.6;
    else if (filteredNodes.length > 300) baseSize *= 0.8;

    const r = baseSize;
    const color = getNodeColor(node);
    const alpha = isHighlighted ? 0.92 : 0.08;

    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);

    // Fill
    const { r: cr, g: cg, b: cb } = hexToRgb(color.startsWith('#') ? color : '#6366f1');
    ctx.fillStyle = `rgba(${cr}, ${cg}, ${cb}, ${alpha})`;
    ctx.fill();

    // Outline
    if (isHighlighted) {
      ctx.strokeStyle = isPinned ? '#1e293b' : `rgba(${cr}, ${cg}, ${cb}, 0.6)`;
      ctx.lineWidth = isPinned ? 2.5 / globalScale : 1 / globalScale;
      ctx.stroke();
    }

    // Label for large nodes when zoomed in
    if (globalScale > 1.8 && r > 4 && isHighlighted) {
      ctx.font = `${Math.max(3, 9 / globalScale)}px sans-serif`;
      ctx.fillStyle = 'rgba(30, 41, 59, 0.85)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(node.response || '', node.x, node.y + r + 2 / globalScale);
    }
  }, [highlightNodes, pinnedNode, sizeMode, filteredNodes.length, getNodeColor]);

  // ── Link rendering ─────────────────────────────────────────────────
  const getLinkColor = useCallback((link) => {
    if (highlightLinks.size > 0) {
      return highlightLinks.has(link) ? 'rgba(99,102,241,0.5)' : 'rgba(200,200,210,0.02)';
    }
    return 'rgba(180,180,195,0.12)';
  }, [highlightLinks]);

  const getLinkWidth = useCallback((link) => {
    if (highlightLinks.has(link)) return 1.2;
    return 0.3;
  }, [highlightLinks]);

  // ── Tooltips ───────────────────────────────────────────────────────
  const getNodeLabel = useCallback((node) => {
    const attrs = Object.entries(node.attributes || {})
      .filter(([k]) => !['name', 'id'].includes(k.toLowerCase()))
      .slice(0, 4)
      .map(([k, v]) => `<div style="color:#64748b;font-size:11px">${humanizeColumnName(k)}: <span style="color:#334155;font-weight:500">${v}</span></div>`)
      .join('');
    return `<div style="background:white;border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;font-size:12px;max-width:240px;box-shadow:0 4px 16px rgba(0,0,0,0.12);pointer-events:none;color:#1e293b">
      <div style="font-weight:700;font-size:13px;margin-bottom:4px">${node.response}</div>
      <div style="color:#6366f1;font-size:11px;margin-bottom:4px">Community ${node.cluster + 1} · Centrality ${(node.degree_centrality * 100).toFixed(1)}%</div>
      <div style="color:#64748b;font-size:11px;margin-bottom:2px">Confidence: ${(node.confidence * 100).toFixed(0)}% · Extremity: ${(node.opinion_extremity * 100).toFixed(0)}%</div>
      ${attrs}
    </div>`;
  }, []);

  // ── Interaction handlers ───────────────────────────────────────────
  const clearHighlights = useCallback(() => {
    setPinnedNode(null); setHighlightNodes(new Set()); setHighlightLinks(new Set());
  }, []);

  const handleNodeClick = useCallback((node) => {
    if (!node) { clearHighlights(); return; }
    if (pinnedNode?.id === node.id) { clearHighlights(); return; }
    const neighbors = new Set([node.id]);
    const linkSet = new Set();
    graphData.links.forEach(link => {
      const sid = typeof link.source === 'object' ? link.source.id : link.source;
      const tid = typeof link.target === 'object' ? link.target.id : link.target;
      if (sid === node.id || tid === node.id) {
        neighbors.add(sid); neighbors.add(tid); linkSet.add(link);
      }
    });
    setPinnedNode(node); setHighlightNodes(neighbors); setHighlightLinks(linkSet);
  }, [pinnedNode, graphData.links, clearHighlights]);

  const handleCommunityClick = useCallback((clusterId) => {
    const communityNodes = new Set(graphData.nodes.filter(n => n.cluster === clusterId).map(n => n.id));
    const communityLinks = new Set(graphData.links.filter(link => {
      const sid = typeof link.source === 'object' ? link.source.id : link.source;
      const tid = typeof link.target === 'object' ? link.target.id : link.target;
      return communityNodes.has(sid) && communityNodes.has(tid);
    }));
    setPinnedNode(null);
    setHighlightNodes(communityNodes);
    setHighlightLinks(communityLinks);
  }, [graphData]);

  const resetView = useCallback(() => {
    if (graphRef.current) {
      graphRef.current.centerAt(0, 0, 400);
      graphRef.current.zoom(1, 400);
    }
    clearHighlights();
  }, [clearHighlights]);

  const colorOptions = useMemo(() => {
    const opts = [
      { value: 'cluster', label: 'Community' },
      { value: 'response', label: 'Expected Response' },
    ];
    if (data?.filter_dimensions) {
      data.filter_dimensions.forEach(dim => {
        opts.push({ value: dim.name, label: humanizeColumnName(dim.name) });
      });
    }
    return opts;
  }, [data]);

  // ── Cluster-aware force configuration ──────────────────────────────
  const configureForces = useCallback((fg) => {
    if (!fg) return;
    const forceInstance = fg.d3Force;
    if (!forceInstance) return;

    const charge = forceInstance('charge');
    if (charge) charge.strength(-180).distanceMax(400);

    const link = forceInstance('link');
    if (link) {
      link.distance(l => {
        const src = typeof l.source === 'object' ? l.source : graphData.nodes.find(n => n.id === l.source);
        const tgt = typeof l.target === 'object' ? l.target : graphData.nodes.find(n => n.id === l.target);
        if (src?.cluster === tgt?.cluster) return 10;
        return 200;
      });
      link.strength(l => {
        const src = typeof l.source === 'object' ? l.source : graphData.nodes.find(n => n.id === l.source);
        const tgt = typeof l.target === 'object' ? l.target : graphData.nodes.find(n => n.id === l.target);
        if (src?.cluster === tgt?.cluster) return 0.8;
        return 0.005;
      });
    }
  }, [graphData.nodes]);

  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0) {
      configureForces(graphRef.current);
      setTimeout(() => {
        if (graphRef.current?.zoomToFit) graphRef.current.zoomToFit(400, 60);
      }, 2500);
    }
  }, [graphData, configureForces]);

  const graphHeight = expanded ? 800 : 600;

  // ── Loading / Error states ─────────────────────────────────────────
  if (loading) {
    return (
      <Card className="border border-border">
        <CardContent className="py-12 flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Analyzing behavioral clusters...</span>
        </CardContent>
      </Card>
    );
  }

  if (!data?.has_clusters) {
    const reason = data?.reason;
    if (reason === 'social_not_enabled') return null;
    return (
      <Card className="border border-amber-200 bg-amber-50/30">
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-5 h-5 text-amber-600 mx-auto mb-2" />
          <p className="text-sm text-amber-800 font-medium">
            {reason === 'insufficient_data' ? 'Network visualization requires more respondents.' :
             reason === 'low_diversity' ? 'Network visualization requires sufficient behavioral diversity.' :
             'Behavioral cluster analysis is not available for this simulation.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const { clusters, polarization, executive_summary } = data;

  return (
    <div className="space-y-4" data-testid="behavioral-cluster-network">
      {/* Executive Summary */}
      <Card className="bg-gradient-to-r from-indigo-50 to-slate-50 border-indigo-200" data-testid="cluster-executive-summary">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-indigo-900">Community Network Analysis</span>
            <Badge className={cn("text-[10px]",
              polarization.level === 'High' ? 'bg-red-100 text-red-700 border-red-300' :
              polarization.level === 'Moderate' ? 'bg-amber-100 text-amber-700 border-amber-300' :
              'bg-emerald-100 text-emerald-700 border-emerald-300'
            )}>
              {polarization.level} Polarization
            </Badge>
            <Badge variant="outline" className="text-[10px]">{data.n_clusters} communities</Badge>
            <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-700">
              {data.clustering_method === 'louvain' ? 'Louvain' : 'K-Means'}
            </Badge>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed" data-testid="cluster-summary-text">{executive_summary}</p>
        </CardContent>
      </Card>

      {/* Structural Metrics */}
      <StructuralMetrics data={data} />

      {/* Polarization */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Polarization" value={polarization.level}
          sub={`${polarization.between_cluster_divergence}% divergence`}
          color={polarization.level === 'High' ? 'text-red-600' : polarization.level === 'Moderate' ? 'text-amber-600' : 'text-emerald-600'} />
        <MetricCard label="Population" value={data.total_n.toLocaleString()} sub={`${data.vis_n} visualized`} />
        <MetricCard label="Fragmentation" value={polarization.fragmentation_score}
          sub={polarization.fragmentation_score > 3 ? 'Highly fragmented' : polarization.fragmentation_score > 1.5 ? 'Moderately split' : 'Cohesive'} />
        <MetricCard label="Score Variance" value={polarization.score_variance.toFixed(3)} sub="Response spread" />
      </div>

      {/* ── Network Graph (Polinode-style) ─────────────────────────────── */}
      <Card className="border border-slate-200 overflow-hidden">
        {/* Toolbar */}
        <CardHeader className="pb-2 border-b border-slate-100 bg-slate-50/60">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm font-medium text-slate-800">Network Visualization</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={colorMode} onValueChange={setColorMode}>
                <SelectTrigger className="w-40 h-7 text-[11px] bg-white" data-testid="cluster-color-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colorOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={sizeMode} onValueChange={setSizeMode}>
                <SelectTrigger className="w-36 h-7 text-[11px] bg-white" data-testid="cluster-size-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="degree">Size: Centrality</SelectItem>
                  <SelectItem value="extremity">Size: Extremity</SelectItem>
                  <SelectItem value="confidence">Size: Confidence</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterDim} onValueChange={(v) => { setFilterDim(v); setFilterVal('__all__'); }}>
                <SelectTrigger className="w-36 h-7 text-[11px] bg-white" data-testid="cluster-filter-dim">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">No Filter</SelectItem>
                  {(data.filter_dimensions || []).map(d => (
                    <SelectItem key={d.name} value={d.name}>{humanizeColumnName(d.name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {filterDim !== '__all__' && (
                <Select value={filterVal} onValueChange={setFilterVal}>
                  <SelectTrigger className="w-32 h-7 text-[11px] bg-white" data-testid="cluster-filter-val">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All</SelectItem>
                    {filterValues.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}

              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setExpanded(!expanded)} title={expanded ? 'Collapse' : 'Expand'}>
                {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={resetView} title="Reset view" data-testid="reset-view-btn">
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Edge Density */}
          <div className="flex items-center gap-3 mt-2">
            <span className="text-[10px] text-slate-500 whitespace-nowrap">Edges</span>
            <Slider
              value={[edgeDensity]}
              onValueChange={([v]) => setEdgeDensity(v)}
              max={100} min={0} step={5}
              className="w-32"
              data-testid="edge-density-slider"
            />
            <span className="text-[10px] text-slate-400 w-8">{edgeDensity}%</span>
            <span className="text-[10px] text-slate-400">{graphData.links.length} edges</span>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="flex" style={{ height: graphHeight }}>
            {/* ── Community Legend Sidebar ─────────────────────────────── */}
            <div className="w-48 shrink-0 border-r border-slate-100 bg-white overflow-y-auto p-3" data-testid="community-legend-sidebar">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Communities</span>
                <button onClick={() => setShowDetails(!showDetails)} className="text-slate-400 hover:text-slate-600">
                  {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>
              <div className="space-y-1">
                {clusters.map(c => {
                  const color = COMMUNITY_PALETTE[c.id % COMMUNITY_PALETTE.length];
                  const isActive = highlightNodes.size > 0 && graphData.nodes.some(n => n.cluster === c.id && highlightNodes.has(n.id));
                  return (
                    <button
                      key={c.id}
                      className={cn(
                        "w-full text-left rounded-md px-2 py-1.5 transition-colors",
                        isActive ? 'bg-slate-100' : 'hover:bg-slate-50'
                      )}
                      onClick={() => handleCommunityClick(c.id)}
                      data-testid={`community-legend-${c.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-[11px] font-medium text-slate-700">Community {c.id + 1}</span>
                        <span className="ml-auto text-[10px] text-slate-400">{c.pct}%</span>
                      </div>
                      {showDetails && (
                        <div className="ml-5 mt-0.5 space-y-0.5">
                          <p className="text-[10px] text-slate-500">
                            {c.leaning} · {c.dominant_response}
                          </p>
                          {c.dominant_demographics.length > 0 && (
                            <p className="text-[9px] text-slate-400 truncate">
                              {c.dominant_demographics.slice(0, 2).map(d => d.value).join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Color scale legend */}
              {colorMode === 'response' && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-[10px] text-slate-500 mb-1.5">Response Scale</p>
                  <div className="flex items-center gap-1.5 text-[9px] text-slate-500">
                    <span>Negative</span>
                    <div className="flex h-2.5 rounded overflow-hidden flex-1">
                      {[0, 0.25, 0.5, 0.75, 1].map(s => (
                        <div key={s} className="flex-1" style={{ backgroundColor: scoreToColor(s) }} />
                      ))}
                    </div>
                    <span>Positive</span>
                  </div>
                </div>
              )}

              {/* Clear filter */}
              {highlightNodes.size > 0 && (
                <button
                  onClick={clearHighlights}
                  className="mt-3 w-full text-[10px] text-indigo-600 hover:text-indigo-800 font-medium text-center py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 transition-colors"
                  data-testid="clear-highlight-btn"
                >
                  Clear selection
                </button>
              )}
            </div>

            {/* ── Graph Canvas ────────────────────────────────────────── */}
            <div ref={handleContainerRef} className="flex-1 relative bg-[#f8fafc]">
              {dimensions.width > 0 && graphData.nodes.length > 0 && (
                <ForceGraph2D
                  key="fg2d"
                  ref={graphRef}
                  graphData={graphData}
                  nodeId="id"
                  nodeCanvasObject={paintNode}
                  nodePointerAreaPaint={(node, color, ctx) => {
                    const r = sizeMode === 'degree'
                      ? 2 + Math.sqrt(node.degree_centrality || 0.01) * 18
                      : 6;
                    ctx.beginPath();
                    ctx.arc(node.x, node.y, r + 2, 0, 2 * Math.PI);
                    ctx.fillStyle = color;
                    ctx.fill();
                  }}
                  nodeLabel={getNodeLabel}
                  linkColor={getLinkColor}
                  linkWidth={getLinkWidth}
                  linkCurvature={0.1}
                  onNodeClick={handleNodeClick}
                  onBackgroundClick={clearHighlights}
                  warmupTicks={200}
                  cooldownTicks={600}
                  width={Math.max(dimensions.width - 192, 200)}
                  height={graphHeight}
                  backgroundColor="#f8fafc"
                />
              )}

              {/* Pinned node info */}
              {pinnedNode && (
                <div className="absolute top-3 right-3 bg-white shadow-lg border border-slate-200 rounded-lg px-3.5 py-3 text-xs z-10 max-w-[260px]" data-testid="pinned-node-info">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-bold text-slate-900 text-sm">{pinnedNode.response}</span>
                    <button className="text-slate-400 hover:text-slate-700 ml-2 text-base leading-none" onClick={clearHighlights}>&times;</button>
                  </div>
                  <div className="space-y-0.5 text-slate-600">
                    <p>Community <span className="font-semibold text-slate-900">{pinnedNode.cluster + 1}</span></p>
                    <p>Centrality: <span className="font-semibold text-slate-900">{(pinnedNode.degree_centrality * 100).toFixed(1)}%</span></p>
                    <p>Extremity: <span className="font-semibold text-slate-900">{(pinnedNode.opinion_extremity * 100).toFixed(0)}%</span></p>
                    <p>Confidence: <span className="font-semibold text-slate-900">{(pinnedNode.confidence * 100).toFixed(0)}%</span></p>
                  </div>
                  {highlightNodes.size > 1 && (
                    <p className="text-indigo-600 mt-1.5 font-medium">{highlightNodes.size - 1} connected neighbor{highlightNodes.size > 2 ? 's' : ''}</p>
                  )}
                  <div className="mt-1.5 pt-1.5 border-t border-slate-100 space-y-0.5">
                    {Object.entries(pinnedNode.attributes || {}).filter(([k]) => !['name', 'id'].includes(k.toLowerCase())).slice(0, 5).map(([k, v]) => (
                      <p key={k} className="text-slate-500">{humanizeColumnName(k)}: <span className="text-slate-800 font-medium">{v}</span></p>
                    ))}
                  </div>
                </div>
              )}

              {/* Info overlay */}
              <div className="absolute bottom-2 left-2 text-[9px] text-slate-400 flex items-center gap-1 bg-white/80 px-2 py-1 rounded">
                <Info className="w-3 h-3" />
                <span>Drag to pan · Scroll to zoom · Click node or community</span>
                {data.vis_n < data.total_n && <span>· {data.vis_n.toLocaleString()}/{data.total_n.toLocaleString()} nodes</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Community Detail Cards */}
      <ClusterBubbleSummary clusters={clusters} onCommunityClick={handleCommunityClick} />

      {/* Community Detail Cards (text) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="cluster-cards">
        {clusters.map(c => (
          <Card key={c.id} className="border border-border hover:shadow-sm transition-shadow cursor-pointer" onClick={() => handleCommunityClick(c.id)}>
            <CardContent className="pt-3 pb-2.5">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COMMUNITY_PALETTE[c.id % COMMUNITY_PALETTE.length] }} />
                <span className="text-xs font-semibold text-slate-700">Community {c.id + 1}</span>
                <span className="ml-auto text-xs font-bold text-slate-800">{c.pct}%</span>
              </div>
              <div className="space-y-1 text-[11px] text-slate-600">
                <p>Leaning: <span className="font-medium text-slate-800">{c.leaning}</span></p>
                <p>Dominant: <span className="font-medium">{c.dominant_response}</span></p>
                <p>Confidence: <span className="font-medium">{c.avg_confidence}</span></p>
                {c.avg_degree_centrality !== undefined && (
                  <p>Avg Centrality: <span className="font-medium">{(c.avg_degree_centrality * 100).toFixed(1)}%</span></p>
                )}
                {c.dominant_demographics.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {c.dominant_demographics.map((t, i) => (
                      <Badge key={i} variant="outline" className="text-[9px] px-1 py-0">
                        {t.value} ({humanizeColumnName(t.attribute)}) {t.pct}%
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
